import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import IconWhatsapp from '@mui/icons-material/WhatsApp';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { DataProviderInstance } from '../../api/DataProvider';
import { formatLeadPhone } from '../../utils/leadUtils';
import LeadsConfig from '../../consts/LeadsConfig';
import IconAction from '../dashboard/components/shared/IconAction';
import { getNextStatus, getPreviousStatus, stageLabel } from './components/leadStages';
import { leadTemperature, leadResult, relativeTime, fullDate, initialsOf } from './components/leadVisuals';
import DemoTag from '../../components/demo-data/DemoTag';
import { isDemoRecord } from '../../components/demo-data/demoData';
import styles from './LeadCardItem.module.css';

// `onBlockedAction`: quando informado (plano sem funil de leads), a mudança de
// status abre o modal de upgrade em vez de chamar a API.
// `countryCode`: DDI do lead (o mesmo do envio de WhatsApp), para mostrar o telefone completo
const LeadCardItem = ({ leadData, countryCode, onEdit, onUpdate, onWhatssappMessageClick, onConvertToPatient, onBlockedAction = null }) => {
    const { CompanyConfig, UpdateLead } = DataProviderInstance();
    const navigate = useNavigate();

    const { createdAt, name, phone, channel, result, campaignId, status, patientId } = leadData;

    const handleStatusChange = (newStatus) => {
        if (onBlockedAction) { onBlockedAction(); return; }
        UpdateLead({ ...leadData, status: newStatus }, () => onUpdate());
    };

    // Aguardando retorno -> Finalizado: a seta pede só o resultado, sem abrir o popup
    const [finishAnchor, setFinishAnchor] = useState(null);
    const openFinishMenu = (e) => {
        if (onBlockedAction) { onBlockedAction(); return; }
        setFinishAnchor(e.currentTarget);
    };
    const handleFinish = (newResult) => {
        setFinishAnchor(null);
        UpdateLead({ ...leadData, status: LeadsConfig.StatusFinished, result: newResult }, () => onUpdate());
    };

    // Inválido: a seta devolve para Em andamento e desmarca o inválido
    const isInvalid = leadData.valid == '0';
    const handleRestoreInvalid = () => {
        if (onBlockedAction) { onBlockedAction(); return; }
        UpdateLead({
            ...leadData,
            valid: 1,
            status: LeadsConfig.StatusInProgress,
            result: LeadsConfig.ResultPending,
        }, () => onUpdate());
    };

    // Handle case where campaignId might not exist in the campaigns list
    // For new companies, use -1 as "unknown" instead of 0
    const campaignIdSafe = campaignId || -1;
    const campaign = CompanyConfig.LeadsCampaigns.find((x) => x.id == campaignIdSafe + '') || null;

    // A fonte vem do próprio lead quando existir; senão, da campanha vinculada
    const sourceIdSafe = leadData.sourceId || campaign?.sourceId || null;
    const sourceName = sourceIdSafe
        ? (CompanyConfig.LeadsSources.find((x) => x.id == sourceIdSafe + '')?.name || null)
        : null;

    // "Fonte › Campanha", caindo para o canal amigável quando não há origem
    const originLabel = [sourceName, campaign?.name].filter(Boolean).join(' › ')
        || LeadsConfig.GetChannelFriendlyName(channel);

    const isFinished = status == LeadsConfig.StatusFinished;
    const temperature = leadTemperature(createdAt);
    const outcome = leadResult(result, leadData);
    // "Virou paciente" sai no próprio selo do paciente (que abre a ficha)
    const convertedOnly = !!outcome?.converted;
    // Finalizado pinta o card pelo resultado; nas outras etapas, pela temperatura
    const toneClass = isFinished ? styles[outcome?.tone || 'neutral'] : styles[temperature.tone];

    const friendlyPhone = formatLeadPhone(phone, countryCode);
    const title = name || friendlyPhone;
    const initials = initialsOf(name);

    const canGoBack = !isInvalid && status != LeadsConfig.StatusNew;
    const canGoForward = !isInvalid && status != LeadsConfig.StatusWaitingResponse && status != LeadsConfig.StatusFinished;
    const canFinish = !isInvalid && status == LeadsConfig.StatusWaitingResponse;

    const stop = (e) => e.stopPropagation();

    const onPatientChipClickHandler = (e) => {
        e.stopPropagation();
        navigate(`/patients/${patientId}`);
    };

    const onCardKeyDown = (e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEdit(leadData);
        }
    };

    const TemperatureIcon = temperature.Icon;

    return (
        <article
            className={`${styles.card} ${toneClass}`}
            onClick={() => onEdit(leadData)}
            onKeyDown={onCardKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Abrir lead ${title}`}
        >
            <div className={styles.top}>
                <span className={styles.avatar} aria-hidden="true">
                    {initials || <PersonOutlineRoundedIcon style={{ fontSize: 18 }} />}
                </span>
                <div className={styles.identity}>
                    <span className={styles.name}>{title}</span>
                    {name && <span className={styles.phone}>{friendlyPhone}</span>}
                    {isDemoRecord(leadData) && <span style={{ alignSelf: 'flex-start', marginTop: 2 }}><DemoTag compact /></span>}
                </div>
                <div className={styles.actions} onClick={stop}>
                    {!patientId && (
                        <IconAction title="Cadastrar paciente" onClick={() => onConvertToPatient && onConvertToPatient(leadData)} tone="success">
                            <PersonAddAlt1OutlinedIcon style={{ fontSize: 17 }} />
                        </IconAction>
                    )}
                    <IconAction title="Conversar no WhatsApp" onClick={() => onWhatssappMessageClick(leadData)} tone="whatsapp">
                        <IconWhatsapp style={{ fontSize: 17 }} />
                    </IconAction>
                </div>
            </div>

            <div className={styles.meta}>
                {originLabel && (
                    <span className={styles.metaItem}>
                        <CampaignOutlinedIcon className={styles.metaIcon} />
                        <span className={styles.metaText}>{originLabel}</span>
                    </span>
                )}
                <Tooltip title={fullDate(createdAt)} arrow>
                    <span className={styles.metaItem}>
                        <ScheduleRoundedIcon className={styles.metaIcon} />
                        <span className={styles.metaText}>{relativeTime(createdAt)}</span>
                    </span>
                </Tooltip>
            </div>

            <div className={styles.footer}>
                <div className={styles.tags}>
                    {!isFinished && (
                        <Tooltip title={temperature.hint} arrow>
                            <span className={`${styles.tag} ${styles.tagTemperature}`}>
                                <TemperatureIcon className={styles.tagIcon} />
                                {temperature.label}
                            </span>
                        </Tooltip>
                    )}
                    {outcome && !convertedOnly && (
                        <Tooltip title={outcome.hint || ''} arrow>
                            <span className={`${styles.tag} ${outcome.tone === 'success' ? styles.tagSuccess : styles.tagFailure}`}>
                                <outcome.Icon className={styles.tagIcon} />
                                {outcome.label}
                            </span>
                        </Tooltip>
                    )}
                    {patientId && (
                        <Tooltip title="Abrir ficha do paciente" arrow>
                            <button type="button" className={`${styles.tag} ${styles.tagPatient}`} onClick={onPatientChipClickHandler}>
                                <HowToRegOutlinedIcon className={styles.tagIcon} />
                                {convertedOnly ? 'Virou paciente' : 'Paciente'}
                            </button>
                        </Tooltip>
                    )}
                </div>
                {isInvalid && (
                    <div className={styles.move} onClick={stop}>
                        <IconAction title={`Voltar para ${stageLabel(LeadsConfig.StatusInProgress)}`} onClick={handleRestoreInvalid}>
                            <ChevronLeftRoundedIcon style={{ fontSize: 20 }} />
                        </IconAction>
                    </div>
                )}
                {(canGoBack || canGoForward || canFinish) && (
                    <div className={styles.move} onClick={stop}>
                        {canGoBack && (
                            <IconAction title={`Voltar para ${stageLabel(getPreviousStatus(status))}`} onClick={() => handleStatusChange(getPreviousStatus(status))}>
                                <ChevronLeftRoundedIcon style={{ fontSize: 20 }} />
                            </IconAction>
                        )}
                        {canGoForward && (
                            <IconAction title={`Avançar para ${stageLabel(getNextStatus(status))}`} onClick={() => handleStatusChange(getNextStatus(status))}>
                                <ChevronRightRoundedIcon style={{ fontSize: 20 }} />
                            </IconAction>
                        )}
                        {canFinish && (
                            <IconAction title={`Avançar para ${stageLabel(LeadsConfig.StatusFinished)}`} onClick={openFinishMenu}>
                                <ChevronRightRoundedIcon style={{ fontSize: 20 }} />
                            </IconAction>
                        )}
                    </div>
                )}
                {/* O Menu fica num portal, mas o clique ainda borbulha até o card: o stop evita abrir o popup */}
                <Menu
                    anchorEl={finishAnchor}
                    open={!!finishAnchor}
                    onClose={() => setFinishAnchor(null)}
                    onClick={stop}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem onClick={() => handleFinish(LeadsConfig.ResultSuccess)}>
                        <ListItemIcon><EventAvailableOutlinedIcon fontSize="small" style={{ color: '#15803D' }} /></ListItemIcon>
                        Agendado
                    </MenuItem>
                    <MenuItem onClick={() => handleFinish(LeadsConfig.ResultFailure)}>
                        <ListItemIcon><EventBusyOutlinedIcon fontSize="small" style={{ color: '#B42318' }} /></ListItemIcon>
                        Não agendado
                    </MenuItem>
                </Menu>
            </div>
        </article>
    );
};

export default LeadCardItem;
